/**
 * `.rl-rail` — the column of reels on the left of Edit › Reels, and the control
 * that chooses which one the detail beside it is editing (CAE-1552).
 *
 * The ARIA tabs pattern, vertically, the same way `SegmentedTabs` does it
 * horizontally: a roving tabindex so Tab enters the rail once and leaves it, and
 * the arrows move within. The handoff draws plain buttons, which is the right
 * *look* and the wrong semantics — "pick one of these, and the panel next to it
 * changes" is what a tablist is, and the alternative is four Tab presses to step
 * past a rail on the way to the outcome list.
 *
 * > **Values from `.rl-rail*` in prototype/modals.css, not the ticket's prose.**
 * > The ticket describes the selected item as accent-soft with a 3px accent left
 * > border, and its padding as `12px 14px`; the stylesheet draws it as white on
 * > the rail's `#fafbfc` wash with a `--line-strong` edge and a hairline lift, at
 * > `10px 12px`. Same for the dot (8px, not 9) and the type (13.5/11.5, not
 * > 14/12.5). The stylesheet wins — CAE-1537's rule — and the divergences are
 * > listed in prototype/README.md.
 */
import { Fragment, useRef, type KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { Theme } from '@mui/material/styles';
import type { ReelSlot, ReelSlotState } from './reel-slots';

/** `.rl-rail-dot[data-state]` — success, or warn when the slot owes anything. */
const dotColour = (theme: Theme, state: ReelSlotState): string =>
  state === 'ok' ? theme.tokens.color.success : theme.tokens.color.warn;

/**
 * The same line the rail draws, as one string for the tab's `aria-label`.
 *
 * Built from the same `slot.faults` as the visible line rather than written out
 * again, so the sentence a screen reader hears cannot drift from the one on
 * screen.
 */
const railMetaSentence = (slot: ReelSlot): string =>
  [
    ...(slot.stats.options === 0 ? [] : [`${slot.stats.enabled} of ${slot.stats.options} enabled`]),
    ...slot.faults,
  ].join(', ');

/**
 * `.rl-rail-meta` — what the reel is, in one line: its count, then whatever is
 * wrong with it.
 *
 * The counts are the same `reelStats` the breakdown strip and the Setup Rail
 * read, and the faults are `reel-slots.ts`'s, so the rail cannot flag a reel the
 * step above it calls fine — nor call one fine that the step flags.
 *
 * **No count of nothing.** A reel with no options on it leads with its fault
 * alone; "0 of 0 enabled" in front of it would be an arithmetic of an empty
 * reel. That is both the reel nobody has created ("not created") and the one
 * someone named and left ("needs 2 enabled").
 */
const RailMeta = ({ slot }: { slot: ReelSlot }) => {
  const hasCount = slot.stats.options > 0;

  return (
    <>
      {hasCount ? (
        <Box component="span">
          {slot.stats.enabled} of {slot.stats.options} enabled
        </Box>
      ) : null}
      {slot.faults.map((fault, index) => (
        <Fragment key={fault}>
          {/* The dot joins two segments; it is not a bullet in front of one. */}
          {hasCount || index > 0 ? (
            <Box
              aria-hidden
              component="span"
              sx={(theme: Theme) => ({
                width: '3px',
                height: '3px',
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: theme.tokens.componentColor.dotSeparator,
              })}
            />
          ) : null}
          <Box
            component="span"
            sx={(theme: Theme) => ({ color: theme.tokens.color.warn, fontWeight: 500 })}
          >
            {fault}
          </Box>
        </Fragment>
      ))}
    </>
  );
};

export interface ReelSelectorRailProps {
  slots: readonly ReelSlot[];
  /** The reel the detail column is editing. */
  selected: number;
  onSelect: (reelNumber: number) => void;
  /** The detail column's id, for each tab's `aria-controls`. */
  panelId: string;
  /** Namespaces each tab's id so the panel can name the tab that opened it. */
  tabListId: string;
}

export const ReelSelectorRail = ({
  slots,
  selected,
  onSelect,
  panelId,
  tabListId,
}: ReelSelectorRailProps) => {
  const matched = slots.findIndex((slot) => slot.reelNumber === selected);
  // Falls back to the first slot so Tab always has a way into the rail, even
  // while `selected` names a reel the payload has not resolved yet.
  const selectedIndex = matched === -1 ? 0 : matched;
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0) return;

    event.preventDefault();
    // Wraps, like the segmented strip: the rail is a ring, and stopping at the
    // ends would make Reel 3 the awkward one to reach from Reel 1.
    const next = (selectedIndex + step + slots.length) % slots.length;
    onSelect(slots[next]!.reelNumber);
    // Focus follows selection, as the tabs pattern requires — without it focus
    // is stranded on a tab that has just become `tabindex="-1"`.
    tabRefs.current[next]?.focus();
  };

  return (
    <Box
      component="aside"
      role="tablist"
      aria-label="Reels"
      aria-orientation="vertical"
      onKeyDown={onKeyDown}
      sx={(theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '16px',
        backgroundColor: theme.tokens.color.surfaceMuted,
        /*
         * The frame's vertical divider. On the rail rather than between the two
         * columns because the grid has no gap to put one in — `.rl-frame` is
         * `gap: 0` so the rail's wash runs to the divider and the detail's
         * surface starts at it.
         */
        borderRight: theme.tokens.border.default,
      })}
    >
      {slots.map((slot, index) => {
        const isSelected = slot.reelNumber === selected;

        return (
          <ButtonBase
            key={slot.reelNumber}
            ref={(node: HTMLButtonElement | null) => {
              tabRefs.current[index] = node;
            }}
            role="tab"
            id={`${tabListId}-${slot.reelNumber}`}
            /*
             * The number, then whatever the operator has renamed it to, then
             * what is wrong with it — because a renamed reel otherwise has its
             * number nowhere in its accessible name, and the readiness hints
             * every other surface shows are numbered ("Create Reel 3", "add at
             * least 2 enabled options to Reel 1"). Without this a screen-reader
             * user hearing one has nothing in the rail to match it against.
             * `aria-label` rather than visible text: the number is already on
             * screen for anyone looking, as the title of an unnamed reel and as
             * the `Reel N` placeholder behind a named one.
             */
            aria-label={`Reel ${slot.reelNumber}${
              slot.title === `Reel ${slot.reelNumber}` ? '' : ` — ${slot.title}`
            }, ${railMetaSentence(slot)}`}
            aria-selected={isSelected}
            aria-controls={panelId}
            tabIndex={index === selectedIndex ? 0 : -1}
            onClick={() => onSelect(slot.reelNumber)}
            sx={(theme: Theme) => ({
              display: 'flex',
              alignItems: 'flex-start',
              /*
               * Against `ButtonBase`, which centres its content because most
               * buttons are a label in the middle of one. A rail item is a list
               * row — dot, title, meta — and centring set each row adrift by
               * half the width its own text happened to leave over, so *Reel 2*
               * and *Not configured* sat in the middle of the rail while the
               * longer *Reel 1* line, nearly filling the item, looked left
               * aligned. `textAlign: left` below does not reach it: the stray
               * axis is the flex one.
               */
              justifyContent: 'flex-start',
              gap: '10px',
              padding: '10px 12px',
              borderRadius: `${theme.tokens.radius.insetRow}px`,
              textAlign: 'left',
              border: `${theme.tokens.border.width} solid transparent`,
              transition: 'background 0.12s, box-shadow 0.12s',
              ...(isSelected
                ? {
                    backgroundColor: theme.tokens.color.surface,
                    borderColor: theme.tokens.color.lineStrong,
                    boxShadow: theme.tokens.shadow.card,
                  }
                : {
                    '&:hover': {
                      backgroundColor: theme.tokens.color.surface,
                      borderColor: theme.tokens.color.line,
                    },
                  }),
            })}
          >
            <Box
              aria-hidden
              component="span"
              sx={(theme: Theme) => ({
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                flexShrink: 0,
                // `.rl-rail-dot` is top-aligned rather than centred: a two-line
                // item would otherwise float its dot between the title and the
                // meta, beside neither.
                marginTop: '6px',
                backgroundColor: dotColour(theme, slot.state),
              })}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
              <Box
                component="span"
                sx={(theme: Theme) => ({
                  fontSize: '13.5px',
                  fontWeight: 600,
                  letterSpacing: '-0.005em',
                  color: theme.tokens.color.ink,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                })}
              >
                {slot.title}
              </Box>
              <Box
                sx={(theme: Theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '11.5px',
                  color: theme.tokens.color.ink3,
                  fontVariantNumeric: 'tabular-nums',
                })}
              >
                <RailMeta slot={slot} />
              </Box>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};
